import usePageMetadata from '@hooks/usePageMetadata'
import Head from 'next/head'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from 'react-query'
import {
  ArticleSchemaImage,
  blickFBPage,
  blickInstaPage,
  blickTWPage,
  createAuthor,
  createPublisher,
  getImageSet,
  getParsedAuthors,
} from '@utils/schema'
import config from '@config'

const {
  backend: { baseUrl },
} = config

type ArticleBodyObject = {
  id: string
  text: string
}
export interface ArticleSchemaData {
  articleBody: ArticleBodyObject[]
  description: string
}

export interface FilteredArticleSchemaImage extends ArticleSchemaImage {
  url: string
}

const ArticleSchema: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const unsubscribeFnRef = useRef<() => void>()
  const queryCache = queryClient.getQueryCache()
  const [articleBodyText, setArticleBodyText] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const pageMetadata = usePageMetadata()

  const updateArticleSchema = useCallback(
    (args: any) => {
      if (
        args.query.queryKey === 'articleSchemaData' &&
        args.type === 'queryUpdated'
      ) {
        const data =
          queryClient.getQueryData<ArticleSchemaData>('articleSchemaData')
        if (!!data?.articleBody?.length) {
          const idsArray = data.articleBody.map(({ id }) => id)

          const finalText = data.articleBody
            .filter(({ id }, index) => idsArray.indexOf(id) === index)
            .map(({ text }) => text)
            .join(' ')
          setArticleBodyText(finalText)
        }

        if (data?.description) {
          setDescription(data.description)
        }
      }
    },
    [queryClient]
  )

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(updateArticleSchema)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [queryCache, updateArticleSchema])

  const {
    htmlTitle,
    title,
    url,
    lastModifiedDate,
    publishedDate,
    teaser,
    authors,
  } = pageMetadata

  const imageSet = useMemo(() => getImageSet(teaser?.image), [teaser?.image])
  const preparedAuthors = useMemo(() => getParsedAuthors(authors), [authors])

  return (
    <Head>
      <script
        key="article"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            mainEntityOfPage: `${baseUrl}${url}`,
            headline: htmlTitle || title,
            dateModified: lastModifiedDate,
            datePublished: publishedDate,
            description,
            image: imageSet,
            sameAS: [blickFBPage, blickTWPage, blickInstaPage],
            ...(!!articleBodyText ? { articleBody: articleBodyText } : {}),
            author: !preparedAuthors.length
              ? createAuthor()
              : preparedAuthors.map((author) => createAuthor(author)),
            publisher: createPublisher(),
          }),
        }}
      />
    </Head>
  )
}

export default ArticleSchema
